import React from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/common/AnimatedSection';

// 样式组件
const OverviewContainer = styled('div', {
  padding: '0',
  maxWidth: '100%',
});

// 服务概述区域
const ServicesSection = styled('div', {
  padding: '80px 0',
  
  '& h2': {
    fontSize: '36px',
    fontWeight: '700',
    margin: '0 0 40px',
    textAlign: 'center',
    color: '#121b33',
  },
});

const ServiceCards = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
  gap: '30px',
  margin: '40px 0',
  
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const ServiceCard = styled('div', {
  borderRadius: '15px',
  overflow: 'hidden',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  backgroundColor: 'white',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  },
});

// 服务卡片图片区域
const ServiceCardImage = styled('div', {
  width: '100%',
  height: '160px',
  background: 'linear-gradient(135deg, #3563E9 0%, #6C5CE7 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: '48px',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 70% 30%, rgba(255, 255, 255, 0.2) 0%, transparent 60%)',
  }
});

// 服务卡片内容区域
const ServiceCardContent = styled('div', {
  padding: '25px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  
  '& h3': {
    fontSize: '20px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#1F2937',
    lineHeight: '1.4',
  },
  
  '& p': {
    fontSize: '15px',
    color: '#4B5563',
    lineHeight: '1.6',
    marginBottom: '20px',
    flex: 1,
  },
});

// 服务卡片链接
const ServiceCardLink = styled('a', {
  display: 'inline-block',
  marginTop: 'auto',
  color: '#3563E9',
  fontWeight: '500',
  textDecoration: 'none',
  fontSize: '14px',
  
  '&:hover': {
    textDecoration: 'underline',
  }
});

const FeaturesSection = styled('div', {
  background: '#0A0F1A',
  padding: '80px 0',
  
  '& h2': {
    fontSize: '36px',
    fontWeight: '700',
    marginBottom: '20px',
    textAlign: 'center',
    color: '#ffffff',
  },
  
  '& p.section-intro': {
    fontSize: '18px',
    lineHeight: '1.6',
    color: '#666',
    maxWidth: '800px',
    margin: '0 auto 50px',
    textAlign: 'center',
  },
});

const FeaturesGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '30px',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 20px',
  
  '@media (max-width: 640px)': {
    gridTemplateColumns: '1fr',
  },
});

// 优化的智能功能卡片样式 - 参考blog卡片设计
const FeatureCard = styled('div', {
  borderRadius: '15px',
  overflow: 'hidden',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  backgroundColor: 'white',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  },
});

// 功能卡片图标区域
const FeatureCardIcon = styled('div', {
  width: '100%',
  height: '80px',
  background: 'linear-gradient(135deg, #3563E9 0%, #6C5CE7 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: '28px',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 70% 30%, rgba(255, 255, 255, 0.2) 0%, transparent 60%)',
  }
});

// 功能卡片内容区域
const FeatureCardContent = styled('div', {
  padding: '25px',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  
  '& h3': {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '15px',
    color: '#1F2937',
    lineHeight: '1.4',
  },
  
  '& p': {
    fontSize: '15px',
    color: '#4B5563',
    lineHeight: '1.6',
    margin: 0,
    flex: 1,
  },
});

const ServicesButton = styled('a', {
  display: 'block',
  width: 'fit-content',
  padding: '14px 32px',
  backgroundColor: '#3563E9',
  color: 'white',
  borderRadius: '4px',
  textDecoration: 'none',
  fontWeight: '600',
  fontSize: '16px',
  transition: 'all 0.3s ease',
  margin: '50px auto 0',
  textAlign: 'center',
  cursor: 'pointer',
  
  '&:hover': {
    backgroundColor: '#2854d8',
    transform: 'translateY(-2px)',
    boxShadow: '0 5px 15px rgba(53, 99, 233, 0.4)',
  },
});

// 主要功能卡片组件
const MainFeatureCard = styled('div', {
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  margin: '50px 0 80px',
  backgroundColor: 'white',
  
  '@media (min-width: 1024px)': {
    flexDirection: 'row',
  },
  
  '& .content': {
    padding: '40px',
    flex: 1,
    
    '& h2': {
      fontSize: '32px',
      fontWeight: '700',
      marginBottom: '20px',
      textAlign: 'left',
      color: '#1F2937',
    },
    
    '& p': {
      fontSize: '17px',
      lineHeight: '1.7',
      color: '#4B5563',
      marginBottom: '25px',
    },
    
    '& .stats': {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '20px',
      marginTop: '30px',
      
      '& .stat-item': {
        padding: '20px',
        borderRadius: '12px',
        background: '#F8FAFC',
        textAlign: 'center',
        
        '& h4': {
          fontSize: '36px',
          fontWeight: '700',
          color: '#3563E9',
          margin: '0 0 10px',
        },
        
        '& p': {
          fontSize: '15px',
          color: '#6B7280',
          margin: 0,
          fontWeight: '500',
        }
      },
    },
  },
  
  '& .image': {
    flex: 1,
    minHeight: '350px',
    background: 'linear-gradient(135deg, #3563E9 0%, #6C5CE7 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
    },
    
    '& img': {
      maxWidth: '70%',
      maxHeight: '250px',
      zIndex: 1,
      position: 'relative',
    },
  },
});

// AI技术概览页面组件
const AiOverview: React.FC = () => {
  const { t } = useTranslation();
  
  // AI服务卡片数据
  const services = [
    {
      icon: "⚡",
      title: t('ai.services.marketAnalysis.title', '实时市场数据监测与预测'),
      description: t('ai.services.marketAnalysis.description', '通过AI算法分析主流市场币价走势，预测最优挖矿时间段，帮助矿工在最佳时机获取收益。'),
      percentage: '520%',
    },
    {
      icon: "🔍",
      title: t('ai.services.powerDistribution.title', '智能算力动态分配'),
      description: t('ai.services.powerDistribution.description', '基于网络难度调整矿机功率，在保持高效挖矿的同时降低能耗，实现收益最大化。'),
      percentage: '20%',
    },
    {
      icon: "📈",
      title: t('ai.services.revenueModel.title', '收益风险平衡模型'),
      description: t('ai.services.revenueModel.description', '建立多币种组合挖矿策略，分散市场风险，保持稳定收益流，即使在市场波动时期也能获得可预期收益。'),
      comingSoon: true,
    },
  ];

  // AI功能列表数据
  const features = [
    {
      icon: "📊",
      title: t('ai.features.marketAnalysis.title', '动态分析主流币价格走势'),
      description: t('ai.features.marketAnalysis.description', '智能识别价格趋势，提前调整挖矿策略，确保在最有利时段进行挖矿。'),
    },
    {
      icon: "⏱️",
      title: t('ai.features.timingOptimization.title', '预测最优挖矿时间段'),
      description: t('ai.features.timingOptimization.description', '基于历史数据和市场走势预测最佳挖矿时机，自动调整挖矿计划。'),
    },
    {
      icon: "🔄",
      title: t('ai.features.networkAdjustment.title', '基于网络难度调整矿机功率'),
      description: t('ai.features.networkAdjustment.description', '根据区块链网络难度实时调整挖矿功率，平衡收益与能耗比例。'),
    },
    {
      icon: "📱",
      title: t('ai.features.monitoring.title', '实时监控矿机运行状态'),
      description: t('ai.features.monitoring.description', '全天候监测设备健康状况，确保矿机处于最佳工作状态。'),
    },
    {
      icon: "🔧",
      title: t('ai.features.maintenance.title', '预测性维护故障预警'),
      description: t('ai.features.maintenance.description', '提前预警可能的设备故障，减少停机时间，最大化挖矿时长。'),
    },
    {
      icon: "🌡️",
      title: t('ai.features.tempControl.title', '动态调节矿场散热系统'),
      description: t('ai.features.tempControl.description', '智能控制环境温度，优化散热系统效率，延长设备寿命。'),
    },
  ];
  
  return (
    <OverviewContainer id="services">
      <AnimatedSection direction="up" delay={0.1}>
        <MainFeatureCard>
          <div className="content">
            <h2>{t('ai.mainFeature.title', 'AI算法助力矿机获得最大收益')}</h2>
            <p>{t('ai.mainFeature.description', 'BestMine的AI挖矿技术利用人工智能实时分析市场数据、网络难度和设备状态，自动调整挖矿参数以最大化回报。我们的智能系统能优化能源使用，降低成本，同时保持高哈希率，让您的挖矿获得显著提升。')}</p>
            
            <div className="stats">
              <div className="stat-item">
                <h4>30%</h4>
                <p>{t('ai.mainFeature.stats.efficiency', '平均效率提升')}</p>
              </div>
              <div className="stat-item">
                <h4>25%</h4>
                <p>{t('ai.mainFeature.stats.energy', '能耗降低')}</p>
              </div>
              <div className="stat-item">
                <h4>99.9%</h4>
                <p>{t('ai.mainFeature.stats.uptime', '运行稳定性')}</p>
              </div>
            </div>
          </div>
          <div className="image">
            {/* 此处可以放置一个矿机+AI图标的SVG或PNG图像 */}
          </div>
        </MainFeatureCard>
      </AnimatedSection>
      
      <AnimatedSection direction="up" delay={0.2}>
        <ServicesSection>
          <h2 id="ai-services">{t('ai.services.title', 'BITENGINE AI 挖矿服务')}</h2>
          
          <ServiceCards>
            {services.map((service, index) => (
              <ServiceCard key={index}>
                <ServiceCardImage>{service.icon}</ServiceCardImage>
                <ServiceCardContent>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  {service.percentage && (
                    <div style={{ 
                      padding: '8px 16px', 
                      background: 'rgba(53, 99, 233, 0.1)', 
                      borderRadius: '20px', 
                      color: '#3563E9', 
                      fontWeight: 600, 
                      display: 'inline-block',
                      marginBottom: '15px'
                    }}>
                      {t('ai.services.performance', '平均性能提升高达 {{percentage}}', { percentage: service.percentage })}
                    </div>
                  )}
                  {service.comingSoon && (
                    <div style={{ 
                      padding: '8px 16px', 
                      background: 'rgba(107, 114, 128, 0.1)', 
                      borderRadius: '20px', 
                      color: '#6B7280', 
                      fontWeight: 600, 
                      display: 'inline-block',
                      marginBottom: '15px'
                    }}>
                      {t('ai.services.comingSoon', '即将推出')}
                    </div>
                  )}
                  <ServiceCardLink href="#learn-more">
                    {t('common.learnMore', '了解更多')} →
                  </ServiceCardLink>
                </ServiceCardContent>
              </ServiceCard>
            ))}
          </ServiceCards>

          <ServicesButton as={Link} to="/ai/algorithm">
            {t('ai.services.learnMore', '了解更多 AI 算法')}
          </ServicesButton>
        </ServicesSection>
      </AnimatedSection>
      
      <AnimatedSection direction="up" delay={0.3}>
        <FeaturesSection>
          <h2>{t('ai.features.title', 'AI 挖矿智能功能')}</h2>
          <p className="section-intro">
            {t('ai.features.intro', 'BITENGINE AI 系统集成了多种智能功能，从市场分析到设备维护，全方位提升挖矿体验和收益。')}
          </p>
          
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureCardIcon>{feature.icon}</FeatureCardIcon>
                <FeatureCardContent>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </FeatureCardContent>
              </FeatureCard>
            ))}
          </FeaturesGrid>

          <ServicesButton as={Link} to="/ai/revenue">
            {t('ai.features.exploreRevenue', '探索收益优化方案')}
          </ServicesButton>
        </FeaturesSection>
      </AnimatedSection>
    </OverviewContainer>
  );
};

export default AiOverview; 